import BusinessIcon from "@mui/icons-material/Business";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { DataWindow, useDataWindows, useMetaData } from "../../api/useMetaDataApi";
import {
  useAdditionalCoverageUrl,
  useMarketSummary,
  usePlanData,
  useTopHealthcareProviders,
} from "../../api/usePayerProfileApi";
import BreadCrumbs from "../../components/BreadCrumbs/BreadCrumbs";
import GeoSelect from "../../components/GeoSelect/GeoSelect";
import PageHeader from "../../components/Headers/PageHeader";
import MinimalSelect from "../../components/Inputs/MinimalSelect";
import Select from "../../components/Inputs/Select";
import LastUpdate from "../../components/LastUpdate/LastUpdate";
import { useModal } from "../../components/Modal/useModal";
import {
  useClientConfiguration,
  useClientConfigurationLoaded,
  useClientIndicationFiltersconfiguration,
  useClientPlanProfileConfiguration,
  useClientPlanProfileMarketSummaryConfiguration,
  useIndicationBasedPlanDataConfiguration,
} from "../../data/global/useProductConfiguration";
import { usePayerLandscapeDispatch, usePayerLandscapeState } from "../../data/payerLandscape/hooks";
import { SortDirection } from "../../generate";
import useGetRole from "../../hooks/useGetRole";
import { useProducts } from "../../hooks/useProducts";
import i18n from "../../i18n";
import { reconcileIndicationFilter } from "../../utils/filterReconciliation";
import MarketSummaryTable from "./MarketSummaryTable";
import PbmModal from "./PbmModal";
import PlanDataTable from "./PlanDataTable";
import TopHealthcareProviders from "./TopHealthcareProviders";

const PlanProfile: React.FC = () => {
  const { t } = useTranslation();
  const { setModal } = useModal();
  const {
    client: selectedClient,
    id,
    hcpID,
  } = useParams() as { client: string; id: string; hcpID: string | undefined };
  const { data: metaData } = useMetaData(selectedClient);
  const {
    pharmacyBenefitManagers: showPharmacyBenefitManagers,
    indicationFiltering: showIndicationFiltering,
    hideMarketSummary,
  } = useClientPlanProfileConfiguration(selectedClient);
  const { dataKeys: allowedMarketSummaryDataKeys } = useClientPlanProfileMarketSummaryConfiguration(selectedClient);
  const { sharedFilters, detailFilters } = usePayerLandscapeState();
  const { updateDetailFilters, updateSharedFilters } = usePayerLandscapeDispatch();
  const indicationOptions = useClientIndicationFiltersconfiguration(selectedClient);
  // Indication sent to the API: undefined unless it is valid for the client in the URL.
  const indication = showIndicationFiltering
    ? reconcileIndicationFilter(detailFilters.indication, indicationOptions)
    : undefined;
  const configurationLoaded = useClientConfigurationLoaded(selectedClient);
  // Requests wait for the client's configuration and, when it filters by indication, a valid indication.
  const indicationReady = configurationLoaded && (!showIndicationFiltering || indication !== undefined);
  const { data: dataWindows } = useDataWindows(selectedClient);
  const timePeriodOptions = (dataWindows || []).map((dataWindow) => {
    return {
      label: t(`payerLandscape.timePeriodOptions.${dataWindow}`),
      value: dataWindow,
    };
  });
  const defaultDataWindow =
    (dataWindows || []).find((dataWindow) => dataWindow === DataWindow.twelveMonths) || DataWindow.twelveMonths;
  const showTimePeriodSelection = (dataWindows || []).length > 1;
  const planData = usePlanData(
    selectedClient,
    id,
    sharedFilters.timePeriod || defaultDataWindow,
    indication,
    indicationReady,
  );

  const { data: role } = useGetRole(selectedClient);

  const { products: allowedPlanInfoProducts } = useClientConfiguration(selectedClient);
  const products = useProducts(selectedClient);
  const allProducts = products.data || [];
  const planInfoProducts = allProducts.filter((product) => allowedPlanInfoProducts?.includes(product));

  const topHCPStateInformation = {
    client: selectedClient,
    id: id,
    timePeriod: sharedFilters.timePeriod || defaultDataWindow,
    indication,
  };

  const [topHCPSort, setTopHCPSort] = React.useState<{ field: string; direction: SortDirection } | undefined>(
    undefined,
  );

  const handleTopHCPSortChange = React.useCallback(
    (newSort: { field: string; direction: SortDirection } | undefined) => {
      setTopHCPSort(newSort);
    },
    [],
  );

  const topHealthCareProviders = useTopHealthcareProviders(
    selectedClient,
    id,
    sharedFilters.timePeriod || defaultDataWindow,
    hcpID as string,
    indication,
    topHCPSort?.field,
    topHCPSort?.direction,
    indicationReady,
  );
  const marketSummary = useMarketSummary(
    selectedClient,
    id,
    sharedFilters.timePeriod || defaultDataWindow,
    indication,
    indicationReady,
  );

  const footnoteContent = useMemo(() => {
    const baseKey = `planProfile.footnote.${selectedClient}`;
    const defaultKey = `planProfile.footnote.default`;

    if (role) {
      const roleSpecificKey = `${baseKey}_${role}`;

      // Check if role-specific translation exists for this client
      if (i18n.exists(roleSpecificKey)) {
        return t(roleSpecificKey);
      }
    }

    return t([baseKey, defaultKey]);
  }, [t, selectedClient, role]);

  const { dataKeys: allowedPlanDataKeys } = useIndicationBasedPlanDataConfiguration(selectedClient, indication);

  //IF NO VALID INDICATION SET, SET TO FIRST INDICATION AVAILABLE
  useEffect(() => {
    if (showIndicationFiltering && indicationOptions && indicationOptions.length > 0 && indication === undefined) {
      updateDetailFilters({ indication: indicationOptions[0].value });
    }
  }, [updateDetailFilters, indicationOptions, showIndicationFiltering, indication]);

  const additionalCoverageUrl = useAdditionalCoverageUrl(selectedClient, id, indication, indicationReady);

  const hasAdditionalCoverage =
    additionalCoverageUrl.data && !additionalCoverageUrl.error?.message?.includes("No record found");

  //IF INDICATION SET, BUT SHOULDNT BE, SET TO UNDEFINED
  useEffect(() => {
    if (!showIndicationFiltering && detailFilters.indication !== undefined) {
      updateDetailFilters({ indication: undefined });
    }
  }, [updateDetailFilters, showIndicationFiltering, detailFilters.indication]);

  //IF NO PRODUCT SET, SET TO FIRST PRODUCT AVAILABLE
  useEffect(() => {
    if (planInfoProducts.length > 0 && detailFilters.product === "") {
      updateDetailFilters({ product: planInfoProducts[0] });
    }
  }, [updateDetailFilters, planInfoProducts, detailFilters.product]);

  //Set the default timePeriod to last 12 months
  useEffect(() => {
    if (!sharedFilters.timePeriod && (dataWindows || []).length > 0) {
      updateSharedFilters({ timePeriod: defaultDataWindow });
    }
  }, [dataWindows, defaultDataWindow, sharedFilters.timePeriod, updateSharedFilters]);

  return (
    <div>
      <PageHeader title={t("common.planProfile")}>
        {showPharmacyBenefitManagers && (
          <button onClick={() => setModal(<PbmModal />)} className="btn btn-primary w-full hidden">
            <span>{t("planProfile.viewPBM")}</span>
            <BusinessIcon />
          </button>
        )}
      </PageHeader>
      <BreadCrumbs />

      <div className="lg:flex lg:space-x-12 pb-4 lg:pb-0">
        <div className="mb-4 flex-row lg:w-1/3">
          <h2 className="text-brand header-3">{planData?.data?.planName ?? t("common.loading")}</h2>
          {additionalCoverageUrl.isLoading || !hasAdditionalCoverage ? (
            ""
          ) : (
            <a
              className="underline"
              href={additionalCoverageUrl.data ? additionalCoverageUrl.data : ""}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("planProfile.additionalInformation")}
            </a>
          )}
          {planData.data?.pbmVendor === undefined ? "" : planData.data?.pbmVendor}
        </div>

        <div className="flex flex-col lg:flex-row lg:w-2/3 lg:space-x-4">
          <div className="lg:w-3/12">
            {indicationOptions && showIndicationFiltering && (
              <MinimalSelect
                value={detailFilters.indication}
                handleChange={(value) => updateDetailFilters({ indication: value })}
                label={
                  i18n.exists(`payerLandscape.indicationFor.${selectedClient}`)
                    ? t(`payerLandscape.indicationFor.${selectedClient}`)
                    : t("payerLandscape.indicationFor.default")
                }
                options={indicationOptions}
              />
            )}
          </div>
          <div className="lg:w-6/12">
            {showTimePeriodSelection ? (
              <Select
                value={sharedFilters.timePeriod}
                label={t("payerLandscape.timePeriod")}
                options={timePeriodOptions}
                handleChange={(value) => updateSharedFilters({ timePeriod: value })}
              />
            ) : null}
          </div>
          <div className="lg:w-6/12">
            <GeoSelect client={selectedClient} />
          </div>
        </div>
      </div>

      <div>
        <PlanDataTable
          client={selectedClient}
          loading={planData.isLoading}
          planData={planData.data}
          displayKeys={allowedPlanDataKeys}
        />
        {!hideMarketSummary && (
          <MarketSummaryTable
            client={selectedClient}
            loading={marketSummary.isLoading}
            marketData={marketSummary.data}
            displayKeys={allowedMarketSummaryDataKeys}
          />
        )}
        <TopHealthcareProviders
          channel={topHealthCareProviders.data ? (topHealthCareProviders.data[0].channel as string) : ""}
          loading={detailFilters.product === "" ? true : topHealthCareProviders.isLoading}
          hcpData={topHealthCareProviders.data}
          stateInformation={topHCPStateInformation}
          indication={indication}
          sort={topHCPSort}
          onSortChange={handleTopHCPSortChange}
        />
      </div>
      {footnoteContent && (
        <p className="text-xs text-slate-gray pt-2 whitespace-pre-line leading-6 mb-1">{footnoteContent}</p>
      )}
      {metaData && <LastUpdate className={"pb-6"} {...metaData} />}
    </div>
  );
};

export default PlanProfile;
